import {
    GridColumnVisibilityModel,
    GridRenderCellParams,
    type GridColDef,
} from '@mui/x-data-grid-pro'
import { ColumnsByTemplateIdQuery, SortIndexField } from '__generated__/graphql'
import React, {
    createContext,
    Dispatch,
    FC,
    SetStateAction,
    useState,
} from 'react'
import { type RosterCandidateTableViewId } from 'client/candidate-table-temp/components/RosterCandidatesTable/utils'
import { type RosterCandidateTableRowModel } from 'client/candidate-table-temp/hooks/useRosterSearchWithFilters'
import { useViewConfig } from './useViewConfig'

type RosterCandidateTableColumnDefinition =
    GridColDef<RosterCandidateTableRowModel> & {
        /** The ID of the OpenSearch sorting field to apply when sorting with this column. */
        sortIndexField?: SortIndexField
    }

export interface RosterCandidatesColumnsContextValue {
    columnVisibilityModel: GridColumnVisibilityModel
    setColumnVisibilityModel: Dispatch<
        SetStateAction<GridColumnVisibilityModel>
    >
    currentColumnDefinitions: {
        [key: string]: RosterCandidateTableColumnDefinition
    }
    columnDef: RosterCandidateTableColumnDefinition[]
    columnDefaults: ColumnsByTemplateIdQuery['candidateColumns']
}

export interface RosterCandidatesColumnsProviderProps {
    children: React.ReactNode
    ActionsCell: FC<GridRenderCellParams>
    currentView: RosterCandidateTableViewId
    rosterId: string
    canEdit: boolean
}

export const RosterCandidatesColumnsContext =
    createContext<RosterCandidatesColumnsContextValue>(
        {} as RosterCandidatesColumnsContextValue
    )

function buildVisibilityModel(
    columnDefs: Record<string, unknown>,
    defaults: Array<{ internalName: string }>
): GridColumnVisibilityModel {
    const allColumnKeys = Object.keys(columnDefs)
    const visibleColumnKeys = new Set(defaults.map((col) => col.internalName))
    return allColumnKeys.reduce((acc, key) => {
        acc[key] = visibleColumnKeys.has(key)
        return acc
    }, {} as GridColumnVisibilityModel)
}

export const RosterCandidateColumnsProvider: React.FC<
    RosterCandidatesColumnsProviderProps
> = ({ children, ActionsCell, currentView, rosterId, canEdit }) => {
    // SQL search doesn't use SearchFacetInput filters, so provide no-op
    const setActiveFilters = () => {}

    const config = useViewConfig({
        currentView,
        canEdit,
        rosterId,
        ActionsCell,
        setActiveFilters,
    })

    const columnsToUse = config.defaults
    const currentViewColumnDefs = config.definitions

    const [columnVisibilityModel, setColumnVisibilityModel] =
        useState<GridColumnVisibilityModel>(() =>
            buildVisibilityModel(currentViewColumnDefs, columnsToUse)
        )

    const columnDef = columnsToUse.map((col) => ({
        ...currentViewColumnDefs[col.internalName],
        headerName: col.columnName,
    }))

    return (
        <RosterCandidatesColumnsContext.Provider
            value={{
                columnVisibilityModel,
                setColumnVisibilityModel,
                currentColumnDefinitions: config.definitions,
                columnDef,
                columnDefaults: config.defaults,
            }}
        >
            {children}
        </RosterCandidatesColumnsContext.Provider>
    )
}
